/**
 * Restock Global Sort
 *
 * Fetches ALL pages of a collection via the Section Rendering API,
 * sorts products globally (dated first descending, undated last),
 * and renders the correct page client-side. Pagination is handled
 * without full page reloads after initial fetch.
 */
(function () {
  class RestockGlobalSort {
    constructor(container) {
      this.container = container;
      this.sectionId = container.getAttribute('section-id');
      this.grid = container.querySelector('[ref="grid"]');

      // The section hides the grid until `data-restock-sorted` is set, so every
      // bail-out path has to reveal it — otherwise the collection renders blank.
      if (!this.grid || !this.sectionId) {
        this.reveal();
        return;
      }

      // Watchdog: a fetch that never settles would leave the grid hidden
      // behind the skeleton indefinitely. Reveal the server order instead.
      this.watchdog = setTimeout(this.reveal.bind(this), 8000);

      this.perPage = parseInt(container.dataset.perPage) || 24;
      this.serverTotalPages = parseInt(this.grid.dataset.lastPage) || 1;
      this.currentPage = parseInt(new URLSearchParams(window.location.search).get('page')) || 1;
      this.allProducts = [];
      this.isSorting = false;

      this.run();
    }

    /** Reveals the grid by clearing the section's hide-until-sorted flag. */
    reveal() {
      if (this.watchdog) {
        clearTimeout(this.watchdog);
        this.watchdog = null;
      }
      this.container.setAttribute('data-restock-sorted', 'true');
    }

    async run() {
      if (this.serverTotalPages <= 1 && this.currentPage === 1) {
        this.reveal();
        return;
      }

      this.isSorting = true;

      try {
        await this.fetchAllProducts();
        this.sortProducts();

        var totalPages = Math.ceil(this.allProducts.length / this.perPage);
        if (this.currentPage > totalPages) this.currentPage = totalPages;

        var start = (this.currentPage - 1) * this.perPage;
        var pageProducts = this.allProducts.slice(start, start + this.perPage);

        this.replaceGridContent(pageProducts);
        this.grid.dataset.lastPage = totalPages;
        this.rebuildPagination(totalPages);
        this.interceptPaginationClicks();
      } catch (e) {
        // On error, show server-rendered content as-is
      }

      this.reveal();
      this.isSorting = false;
    }

    /* ── Fetch all pages ── */

    async fetchAllProducts() {
      // Collect current page from DOM
      this.collectFromElement(this.grid);

      if (this.serverTotalPages <= 1) return;

      // Use prefetched results if available (started during HTML parsing).
      // Consume it once: the prefetch was fired for the URL the page loaded
      // with, so after a facets re-render (filter/sort change) it points at the
      // previous result set. Later runs must fetch fresh pages instead.
      var prefetch = window.__restockPrefetch;
      window.__restockPrefetch = null;
      var results;

      if (prefetch && prefetch.length > 0) {
        results = await Promise.all(prefetch);
      } else {
        var promises = [];
        for (var p = 1; p <= this.serverTotalPages; p++) {
          if (p === this.currentPage) continue;
          promises.push(this.fetchPage(p));
        }
        results = await Promise.all(promises);
      }

      for (var i = 0; i < results.length; i++) {
        if (results[i]) this.collectFromHTML(results[i]);
      }
    }

    collectFromElement(gridEl) {
      var items = gridEl.querySelectorAll(':scope > .product-grid__item');
      for (var i = 0; i < items.length; i++) {
        this.allProducts.push({
          html: items[i].outerHTML,
          date: items[i].dataset.restockDate || '',
          id: items[i].dataset.productId || ''
        });
      }
    }

    collectFromHTML(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var grid = doc.querySelector('[ref="grid"]');
      if (grid) this.collectFromElement(grid);
    }

    async fetchPage(pageNum) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('page', pageNum.toString());
        url.searchParams.set('section_id', this.sectionId);
        url.hash = '';
        var resp = await fetch(url.toString());
        if (!resp.ok) return null;
        return await resp.text();
      } catch (e) {
        return null;
      }
    }

    /* ── Sort ── */

    sortProducts() {
      // Deduplicate by product ID
      var seen = {};
      var unique = [];
      for (var i = 0; i < this.allProducts.length; i++) {
        var p = this.allProducts[i];
        if (p.id && seen[p.id]) continue;
        if (p.id) seen[p.id] = true;
        unique.push(p);
      }
      this.allProducts = unique;

      // Sort: dated products first (descending), undated last
      this.allProducts.sort(function (a, b) {
        if (a.date && !b.date) return -1;
        if (!a.date && b.date) return 1;
        if (a.date && b.date) {
          if (b.date > a.date) return 1;
          if (b.date < a.date) return -1;
          return 0;
        }
        return 0;
      });
    }

    /* ── Render ── */

    replaceGridContent(products) {
      // Remove existing product items only
      var existing = this.grid.querySelectorAll(':scope > .product-grid__item');
      for (var i = 0; i < existing.length; i++) {
        existing[i].remove();
      }

      // Insert sorted products
      var fragment = document.createDocumentFragment();
      var temp = document.createElement('div');

      for (var i = 0; i < products.length; i++) {
        temp.innerHTML = products[i].html;
        var li = temp.firstElementChild;
        if (li) {
          li.dataset.page = this.currentPage;
          fragment.appendChild(li);
        }
      }

      this.grid.appendChild(fragment);
    }

    /* ── Pagination ── */

    rebuildPagination(totalPages) {
      // Find existing pagination or its container
      var existingNav = this.container.querySelector('.pagination');
      var paginationParent = existingNav ? existingNav.parentNode : null;

      if (totalPages <= 1) {
        if (existingNav) existingNav.style.display = 'none';
        return;
      }

      // Build new pagination HTML
      var current = this.currentPage;
      var html = '<nav class="pagination" aria-label="Pagination">';
      html += '<ul class="pagination__list" role="list">';

      // Previous button
      html += '<li class="pagination__item">';
      if (current > 1) {
        html += '<a href="' + this.buildPageURL(current - 1) + '" aria-label="Previous page" class="pagination__link pagination__link--arrow" data-sort-page="' + (current - 1) + '">';
        html += '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L2 6L7 11" stroke="currentColor" stroke-width="1.5"/></svg>';
        html += '</a>';
      } else {
        html += '<span aria-label="Previous page" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">';
        html += '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L2 6L7 11" stroke="currentColor" stroke-width="1.5"/></svg>';
        html += '</span>';
      }
      html += '</li>';

      // Page numbers
      for (var p = 1; p <= totalPages; p++) {
        var hideOnMobile = (p !== 1 && p !== totalPages && Math.abs(p - current) > 1);
        html += '<li class="pagination__item' + (hideOnMobile ? ' pagination__item--mobile-hide' : '') + '">';
        if (p === current) {
          html += '<span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">' + p + '</span>';
        } else {
          html += '<a href="' + this.buildPageURL(p) + '" aria-label="Page ' + p + '" class="pagination__link pagination__link--page" data-sort-page="' + p + '">' + p + '</a>';
        }
        html += '</li>';
      }

      // Next button
      html += '<li class="pagination__item">';
      if (current < totalPages) {
        html += '<a href="' + this.buildPageURL(current + 1) + '" aria-label="Next page" class="pagination__link pagination__link--arrow" data-sort-page="' + (current + 1) + '">';
        html += '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.5"/></svg>';
        html += '</a>';
      } else {
        html += '<span aria-label="Next page" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">';
        html += '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.5"/></svg>';
        html += '</span>';
      }
      html += '</li>';

      html += '</ul></nav>';

      // Replace or insert
      if (existingNav && paginationParent) {
        existingNav.outerHTML = html;
      }
    }

    interceptPaginationClicks() {
      var self = this;
      var links = this.container.querySelectorAll('[data-sort-page]');

      for (var i = 0; i < links.length; i++) {
        links[i].addEventListener('click', function (e) {
          e.preventDefault();
          var page = parseInt(this.dataset.sortPage);
          if (!page || page === self.currentPage) return;

          self.currentPage = page;

          var start = (page - 1) * self.perPage;
          var pageProducts = self.allProducts.slice(start, start + self.perPage);

          self.replaceGridContent(pageProducts);

          var totalPages = Math.ceil(self.allProducts.length / self.perPage);
          self.rebuildPagination(totalPages);
          self.interceptPaginationClicks();

          // Update URL without reload
          var url = new URL(window.location.href);
          if (page === 1) {
            url.searchParams.delete('page');
          } else {
            url.searchParams.set('page', page.toString());
          }
          history.pushState({ restockPage: page }, '', url.toString());

          // Scroll to grid top
          self.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    buildPageURL(page) {
      var url = new URL(window.location.href);
      if (page === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page.toString());
      }
      return url.pathname + url.search;
    }
  }

  /* ── Init ── */

  var PENDING_SELECTOR = '[data-restock-sort="true"]:not([data-restock-sorted])';

  function init() {
    var container = document.querySelector(PENDING_SELECTOR);
    if (!container) return;

    // Don't re-init if already running
    if (container.dataset.restockInit === 'true') return;
    container.dataset.restockInit = 'true';

    new RestockGlobalSort(container);
  }

  /**
   * Horizon re-renders this section through the Section Rendering API + morph
   * whenever a filter or sort changes. morph() removes attributes that are
   * absent from the freshly rendered markup, so it strips both
   * `data-restock-sorted` and `data-restock-init` from the live element — and
   * the `<script defer>` above never executes a second time (scripts parsed by
   * DOMParser are inert). Without re-initialising here, the section's
   * `:not([data-restock-sorted])` rule leaves the grid `visibility: hidden`
   * behind the skeleton forever after the first filter/sort interaction.
   *
   * Watching the attributes covers every re-render path: `data-restock-sort`
   * reappearing (shopper returned to the default sort) and
   * `data-restock-sorted` being stripped (filters changed).
   */
  var observer = new MutationObserver(function () {
    if (document.querySelector(PENDING_SELECTOR)) init();
  });

  observer.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-restock-sort', 'data-restock-sorted', 'data-restock-init']
  });

  // Handle browser back/forward
  window.addEventListener('popstate', function () {
    var container = document.querySelector('[data-restock-sort="true"]');
    if (container) {
      container.dataset.restockInit = '';
      container.removeAttribute('data-restock-sorted');
      init();
    }
  });

  // Run immediately — defer script executes after HTML parsing, DOM is ready
  init();
})();